/**
 * The single date-window contract for the consolidated Performance surface (#6274).
 *
 * Every consumer — report queries, time-series, labels — derives its window from
 * the toolbar's raw day count through here. This replaces the old per-panel
 * mapping onto four GAQL DURING enums, which collapsed 60/90/180/365/"All time"
 * into one LAST_90_DAYS bucket and let one page mix three different windows.
 *
 * Semantics:
 * - A window of N days is exactly N calendar days INCLUSIVE of today
 *   (start = today − (N−1)), matching the backend's inclusive BETWEEN.
 * - "All time" (0) means ALL_TIME_DAYS — the widest window the surface offers.
 * - Dates are formatted from the user's local calendar day; the old
 *   toISOString() path silently shifted the whole window a day back for
 *   users east of UTC in the evening.
 */

export const ALL_TIME_DAYS = 365;

export interface DateWindow {
  /** Window length in calendar days (never 0 — "All time" is resolved). */
  days: number;
  start_date: string;
  end_date: string;
}

function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Today as a local-calendar ISO date — the custom pickers' upper bound (#6500). */
export function todayISO(now: Date = new Date()): string {
  return localISO(now);
}

/**
 * Google attributes conversions to the click date and backfills them for a few
 * days, so windows touching the most recent days under-report conversions
 * (CPA reads high, ROAS low) until they settle. True when the window's end is
 * within that settling band — drives the chart's disclosure footnote.
 */
export const CONVERSION_LAG_DAYS = 3;
export function windowInConversionLag(endDate: string, now: Date = new Date()): boolean {
  const end = new Date(`${endDate}T00:00:00`).getTime();
  if (!Number.isFinite(end)) return false;
  const today = new Date(`${todayISO(now)}T00:00:00`).getTime();
  return today - end < CONVERSION_LAG_DAYS * 86_400_000;
}

export function dateWindow(selectedDays: number, now: Date = new Date()): DateWindow {
  const days = selectedDays > 0 ? selectedDays : ALL_TIME_DAYS;
  const start = new Date(now);
  start.setDate(now.getDate() - (days - 1));
  return { days, start_date: localISO(start), end_date: localISO(now) };
}

/**
 * A DateWindow from an explicit picker range (#6500). Inclusive of both ends,
 * mirroring dateWindow's inclusive semantics; a backwards range collapses to
 * the single end day rather than producing a negative length.
 */
export function customDateWindow(start: string, end: string): DateWindow {
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  const ms = e.getTime() - s.getTime();
  const days = Math.max(1, Math.round(ms / 86_400_000) + 1);
  if (ms < 0) return { days: 1, start_date: end, end_date: end };
  return { days, start_date: start, end_date: end };
}

/** The surface's one window resolution: the custom picker range when set, else the day-count preset. */
export function resolveDateWindow(selectedDays: number, custom: { start: string; end: string } | null): DateWindow {
  return custom ? customDateWindow(custom.start, custom.end) : dateWindow(selectedDays);
}

export type ComparisonMode = 'none' | 'previous' | 'previous-week' | 'previous-month' | 'previous-year' | string;

/**
 * The compare-period window for a given current window. Same length as the
 * current window in every mode — the old math produced an N-day previous
 * period against an (N+1)-day current one, biasing every delta upward.
 *
 * Shift modes are "the SAME span, shifted back by the unit" (#4309: 11-17 May
 * 2026 → 11-17 May 2025) — deliberately NOT calendar-aligned. "vs Previous
 * month" compares against the same N days ending one month before today, not
 * the previous calendar month; calendar wrap-around (31st → shorter month,
 * Feb 29) resolves via JS Date normalisation. 'previous' (and 'none', where
 * the result is unused because the fetch is disabled) is the N days
 * immediately before the window.
 */
export function previousDateWindow(current: DateWindow, mode: ComparisonMode, now: Date = new Date()): DateWindow {
  const { days } = current;
  const end = new Date(now);
  const start = new Date(now);
  start.setDate(now.getDate() - (days - 1));

  if (mode === 'previous-week' || mode === 'previous-month' || mode === 'previous-year') {
    const shift = (d: Date) => {
      if (mode === 'previous-week') d.setDate(d.getDate() - 7);
      else if (mode === 'previous-month') d.setMonth(d.getMonth() - 1);
      else d.setFullYear(d.getFullYear() - 1);
      return d;
    };
    return { days, start_date: localISO(shift(start)), end_date: localISO(shift(end)) };
  }

  const prevEnd = new Date(start);
  prevEnd.setDate(start.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevEnd.getDate() - (days - 1));
  return { days, start_date: localISO(prevStart), end_date: localISO(prevEnd) };
}
